import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchMe, signOut } from '../api/auth';
import { errorStatus, onUnauthorized } from '../api/client';
import { forgetThisDevice } from './pushDevice';

/**
 * Who is signed in (customer realm only). null = guest — the app still works for browsing,
 * search and menus; sign-in is needed only for addresses, profile and ordering.
 */
export function useSession() {
  const client = useQueryClient();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const value = await fetchMe();
        return value.realm === 'customer' ? value : null;
      } catch (error) {
        if (errorStatus(error) === 401) return null;
        throw error;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  useEffect(
    () =>
      onUnauthorized(() => {
        client.setQueryData(['me'], null);
      }),
    [client],
  );

  const out = useMutation({
    mutationFn: async () => {
      // W18: take this phone off the account's push list while the session still works.
      await forgetThisDevice();
      await signOut();
    },
    onSettled: () => {
      client.removeQueries({ queryKey: ['profile'] });
      client.removeQueries({ queryKey: ['addresses'] });
      client.removeQueries({ queryKey: ['inbox'] });
      client.removeQueries({ queryKey: ['inbox-unread'] });
      client.removeQueries({ queryKey: ['notify-prefs'] });
      client.removeQueries({ queryKey: ['referral'] });
      client.removeQueries({ queryKey: ['campus'] });
      client.removeQueries({ queryKey: ['cashback'] });
      client.setQueryData(['me'], null);
    },
  });

  return {
    loading: me.isLoading,
    failed: me.isError,
    retry: () => void me.refetch(),
    me: me.data ?? null,
    signedIn: Boolean(me.data),
    signOut: () => out.mutate(),
    signingOut: out.isPending,
  };
}
